import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getCategoryMetadata,
  extractMedicationOptions,
  extractMedicationTagGroups,
  MedicationOption,
  MedicationTagGroup
} from "./categoryMetadata";

interface CategoryMetadataContextValue {
  medicationOptions: MedicationOption[];
  medicationTagGroups: MedicationTagGroup[];
  /** Live map from tagName to medicationEnums for fast lookup */
  tagGroupsByName: Map<string, string[]>;
  loading: boolean;
}

const CategoryMetadataContext = createContext<CategoryMetadataContextValue>({
  medicationOptions: [],
  medicationTagGroups: [],
  tagGroupsByName: new Map(),
  loading: false
});

export const CategoryMetadataProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [medicationOptions, setMedicationOptions] = useState<
    MedicationOption[]
  >([]);
  const [medicationTagGroups, setMedicationTagGroups] = useState<
    MedicationTagGroup[]
  >([]);
  const [tagGroupsByName, setTagGroupsByName] = useState<Map<string, string[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCategoryMetadata()
      .then((categories) => {
        if (cancelled) return;
        const options = extractMedicationOptions(categories);
        const groups = extractMedicationTagGroups(categories);
        setMedicationOptions(options);
        setMedicationTagGroups(groups);
        setTagGroupsByName(
          new Map(groups.map((g) => [g.tagName, g.medicationEnums]))
        );
      })
      .catch((err) => {
        console.error("Failed to fetch category metadata:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <CategoryMetadataContext.Provider
      value={{
        medicationOptions,
        medicationTagGroups,
        tagGroupsByName,
        loading
      }}
    >
      {children}
    </CategoryMetadataContext.Provider>
  );
};

export const useCategoryMetadata = () => useContext(CategoryMetadataContext);
